/**
 * La scrivania di chi carica: scegliere la cartella, vedere che cosa c'e'
 * dentro, essere fermato prima del primo byte se manca qualcosa, e poi
 * spedire — potendo chiudere la scheda e tornare.
 *
 * **Prima del primo byte.** Su un pacchetto da decine di gigabyte l'ordine
 * delle due cose — controllare e caricare — vale una giornata di lavoro. Chi
 * carica deve sentirsi dire «camera 2 senza scatti» adesso, non fra due ore.
 *
 * **Si mostra cio' che si e' riconosciuto, non l'elenco dei file.** Novanta
 * nomi non sono una risposta alla domanda «ho scelto la cartella giusta?»;
 * «DU1048300, due camere da 45 scatti, una nuvola, traiettoria presente» lo e'.
 *
 * **L'avanzamento porta un numero.** «Caricamento in corso» per due ore e'
 * indistinguibile da un blocco, e chi guarda rilancia cio' che stava
 * funzionando.
 *
 * **La ripresa non e' un ricordo della pagina.** Cio' che e' arrivato lo dice
 * l'archivio: si richiede lo stato e si manda cio' che manca. Una contabilita'
 * tenuta qui direbbe «mandato» anche per un invio troncato dalla chiusura della
 * scheda.
 *
 * **Le regole stanno al servizio.** La pagina non ricontrolla il manifesto:
 * una seconda copia delle regole divergerebbe da quella che poi rifiuta
 * davvero, e chi carica si troverebbe fermato dopo aver visto un via libera.
 */
import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'

import { newKey, ServiceError } from './api'
import { collect, type Chosen, weight, withPath } from './folder'
import { moment, share as percent, size } from './format'
import { useT } from './i18n/LocaleProvider'
import { ship, type Progress, type Put } from './transfer'
import * as uploads from './uploads'
import type { Closed, Recognised, UploadState } from './uploads'

/**
 * Quanti giri di ripresa prima di fermarsi e dirlo.
 *
 * Non infiniti: se un file non arriva per un motivo che non e' passeggero —
 * l'archivio pieno, una firma che il proxy rifiuta — riprovare in eterno
 * nasconde il guasto dietro una barra che non avanza.
 */
const PASSES = 3

/** Dove si ricorda la sessione aperta, per poterla riprendere. */
function memory(name: string): string {
  return `gridfly.upload.${name}`
}

export function Uploader({ send }: { send?: Put }): JSX.Element {
  const t = useT()
  const [chosen, setChosen] = useState<Chosen | null>(null)
  const [manifest, setManifest] = useState<unknown>(null)
  const [read, setRead] = useState<Recognised | null>(null)
  const [reading, setReading] = useState(false)
  const [open, setOpen] = useState<uploads.Interrupted[]>([])
  const [sending, setSending] = useState(false)
  const [progress, setProgress] = useState<Progress | null>(null)
  const [done, setDone] = useState<Closed | null>(null)
  const [failure, setFailure] = useState<string | null>(null)
  const picker = useRef<HTMLInputElement>(null)
  const [dragging, setDragging] = useState(false)

  // `webkitdirectory` non e' un attributo che React conosca: si mette a mano
  // sull'elemento. E' il solo modo, oggi, di far scegliere una cartella intera
  // invece di novanta file a uno a uno.
  useEffect(() => {
    picker.current?.setAttribute('webkitdirectory', '')
    picker.current?.setAttribute('directory', '')
  }, [])

  async function chose(files: FileList | readonly File[] | null): Promise<void> {
    setRead(null)
    setFailure(null)
    setProgress(null)
    setDone(null)
    setManifest(null)
    if (files === null || files.length === 0) {
      setChosen(null)
      return
    }

    const found = collect(Array.from(files))
    setChosen(found)
    if (found.manifest === null) {
      setFailure(t('upload.noManifest'))
      return
    }

    setReading(true)
    try {
      const declared: unknown = JSON.parse(await found.manifest.text())
      setManifest(declared)
      setRead(await uploads.inspect(found.files, declared))
    } catch (error) {
      setFailure(
        error instanceof ServiceError
          ? error.message
          : t('upload.badJson'),
      )
    } finally {
      setReading(false)
    }
  }

  /**
   * Riprende la sessione di prima, se c'e' ancora.
   *
   * Una sessione dimenticata dal servizio non e' un guasto: si scorda anche
   * qui e se ne apre una nuova, invece di fermare chi carica davanti a un
   * identificatore che non significa piu' niente.
   */
  useEffect(() => {
    let cancelled = false
    void uploads
      .interrupted()
      .then((found) => {
        if (!cancelled) setOpen(found)
      })
      .catch(() => {
        // Un elenco che non si legge non ferma chi vuole cominciare da capo:
        // la ripresa e' un aiuto, non un passaggio obbligato.
        if (!cancelled) setOpen([])
      })
    return () => {
      cancelled = true
    }
  }, [])

  async function resumed(name: string): Promise<UploadState | null> {
    const remembered = window.localStorage.getItem(memory(name))
    if (remembered !== null) {
      try {
        return await uploads.state(remembered)
      } catch {
        window.localStorage.removeItem(memory(name))
      }
    }
    /*
      L'archivio locale e' una scorciatoia, non la ripresa. Da un'altra
      macchina, da un altro browser o dopo una pulizia dei dati non c'e'
      niente da ricordare, e la sessione aperta esiste lo stesso: si cerca
      dove sta davvero, cioe' nel servizio, per nome del pacchetto. Senza
      questo, sessanta gigabyte ripartivano da zero.
    */
    try {
      const aperte = await uploads.interrupted()
      const stessa = aperte.find((one) => one.package_name === name)
      if (stessa === undefined) return null
      return await uploads.state(stessa.upload_id)
    } catch {
      return null
    }
  }

  async function begin(): Promise<void> {
    if (chosen === null || read === null) return
    setSending(true)
    setFailure(null)
    try {
      let state = (await resumed(read.name)) ?? (await uploads.open(chosen.files, manifest, newKey()))
      window.localStorage.setItem(memory(read.name), state.upload_id)

      for (let pass = 0; pass < PASSES && state.missing.length > 0; pass += 1) {
        await ship(state.missing, chosen.blobs, setProgress, send)
        // Che cosa manca ancora lo dice l'archivio, non il conto degli invii
        // andati bene: un PUT accettato e poi perso e' raro ma esiste, e la
        // chiusura lo scoprirebbe comunque — meglio adesso.
        state = await uploads.state(state.upload_id)
      }

      if (state.missing.length > 0) {
        setFailure(t('upload.missingFiles', { n: state.missing.length }))
        return
      }

      const closed = await uploads.close(state.upload_id, newKey())
      window.localStorage.removeItem(memory(read.name))
      setDone(closed)
    } catch (error) {
      setFailure(
        error instanceof ServiceError
          ? error.message
          : t('upload.openSessions'),
      )
    } finally {
      setSending(false)
    }
  }

  return (
    <main className="caricamento">
      <Interrupted open={open} />

      {/* **La zona, non il campo.** Il campo nativo resta — e' cio' che la
          tastiera e il lettore di schermo raggiungono — ma non e' la prima
          cosa che si vede: il suo bottone parla la lingua del navigatore e
          dice «file» di una cartella. Chi carica una volta a settimana trova
          qui una frase, un gesto e, se il navigatore lo permette, un posto
          dove lasciare cadere la cartella. */}
      <div
        className={dragging ? 'zona sopra' : 'zona'}
        onDragOver={(event) => {
          event.preventDefault()
          if (!sending) setDragging(true)
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={(event) => {
          event.preventDefault()
          setDragging(false)
          if (sending) return
          void dropped(event.dataTransfer).then((files) => (files.length > 0 ? chose(files) : undefined))
        }}
      >
        <label className="zona-invito">
          <span className="zona-titolo">{t('upload.zoneTitle')}</span>
          <span className="zona-nota">{t('upload.zoneHint')}</span>
          <input
            ref={picker}
            type="file"
            multiple
            disabled={sending}
            aria-label={t('upload.folder')}
            className="zona-campo"
            onChange={(event) => void chose(event.target.files)}
          />
          <span className="zona-bottone" aria-hidden="true">
            {t('upload.zoneButton')}
          </span>
        </label>
      </div>

      {chosen !== null && (
        <p className="scelta">
          {t('upload.filesIn', { n: chosen.files.length, size: size(weight(chosen.files)) })}
          {chosen.root !== '' && <>{t('upload.inRoot', { root: chosen.root.replace(/\/$/, '') })}</>}
        </p>
      )}

      {chosen !== null && chosen.outside.length > 0 && (
        <p className="fuori" role="note">
          {t('upload.outside', { n: chosen.outside.length })}
        </p>
      )}

      {reading && <p role="status">{t('upload.reading')}</p>}
      {failure !== null && (
        <p role="alert" className="guasto">
          {failure}
        </p>
      )}

      {read !== null && <Summary read={read} />}

      {read !== null && read.problems.length === 0 && done === null && (
        <p>
          <button type="button" className="primario" disabled={sending} onClick={() => void begin()}>
            {sending ? t('upload.sending') : t('upload.send')}
          </button>
        </p>
      )}

      {progress !== null && done === null && <Advance progress={progress} />}
      {done !== null && <Finished closed={done} />}
    </main>
  )
}

/**
 * I caricamenti lasciati aperti.
 *
 * Sta sopra la scelta della cartella perche' e' la cosa da sapere **prima** di
 * ricominciare: un pacchetto da sessanta gigabyte rimandato da capo costa una
 * serata, e la sessione interrotta si ritrova solo se qualcuno la nomina.
 *
 * Non c'e' un bottone «continua»: la ripresa ha bisogno della cartella, che
 * il servizio non ha e il browser non tiene. Si sceglie di nuovo la cartella,
 * e cio' che e' gia' arrivato non riparte — lo dice l'archivio, non la pagina.
 */
function Interrupted({ open }: { open: uploads.Interrupted[] }): JSX.Element | null {
  const t = useT()
  if (open.length === 0) return null
  return (
    <section className="interrotti" aria-label={t('upload.openSessions')}>
      <h2 className="t-panel">{t('upload.openSessions')}</h2>
      <ul>
        {open.map((one) => (
          <li key={one.upload_id}>
            <strong>{one.package_name ?? t('upload.unnamed')}</strong>{' '}
            <span className="t-num">
              {percent(one.bytes_total > 0 ? one.bytes_done / one.bytes_total : 0)}
            </span>{' '}
            <span className="tenue">
              {t('upload.startedAt', { when: moment(one.created_at) })}
              {one.opened_by !== null && t('upload.openedBy', { who: one.opened_by })}
            </span>
          </li>
        ))}
      </ul>
      <p className="tenue">{t('upload.resumeHint')}</p>
    </section>
  )
}

/**
 * L'avanzamento, in oggetti e in byte.
 *
 * Due numeri e non uno: gli oggetti dicono a che punto e' l'elenco, i byte
 * quanto manca in tempo. Su un pacchetto dove un file pesa quanto trecento, il
 * solo conteggio degli oggetti mentirebbe su entrambe le cose.
 */
function Advance({ progress }: { progress: Progress }): JSX.Element {
  const t = useT()
  const share = progress.bytesTotal > 0 ? progress.bytesDone / progress.bytesTotal : 0

  return (
    <section className="avanzamento" aria-label={t('upload.progress')}>
      <p role="status">
        {t('upload.progressStatus', {
          done: progress.done,
          total: progress.total,
          bytesDone: size(progress.bytesDone),
          bytesTotal: size(progress.bytesTotal),
          percent: percent(share),
        })}
      </p>
      <progress value={progress.bytesDone} max={progress.bytesTotal || 1} />
      {progress.failed.length > 0 && (
        <p className="fuori">{t('upload.failedRetry', { n: progress.failed.length })}</p>
      )}
    </section>
  )
}

/** La fine: il pacchetto e' dentro, e il lavoro e' gia' partito da solo. */
function Finished({ closed }: { closed: Closed }): JSX.Element {
  const t = useT()
  return (
    <section className="finito" role="status">
      <h2>{t('upload.doneTitle')}</h2>
      <p>
        {t('upload.doneObjects', { n: closed.objects })} <code>{closed.job_id}</code>{' '}
        {t('upload.doneWorking')}
      </p>
      <p>
        <Link to={`/uploads/${closed.upload_id}/receipt`}>{t('upload.openReceipt')}</Link>
        {' · '}
        <Link to={`/ingest/${closed.job_id}`}>{t('upload.openIngest')}</Link>
      </p>
    </section>
  )
}

/**
 * Che cosa si e' riconosciuto, e che cosa impedisce di cominciare.
 *
 * I problemi stanno sopra il riepilogo: chi carica deve incontrarli prima di
 * scorrere, perche' sono la sola parte che richiede una decisione.
 */
function Summary({ read }: { read: Recognised }): JSX.Element {
  const t = useT()
  const blocked = read.problems.length > 0

  return (
    <section className="riconosciuto">
      {blocked && (
        <div role="alert" className="rifiuto">
          <h2>{t('upload.cannot')}</h2>
          <p>{t('upload.cannotIntro')}</p>
          <ul>
            {read.problems.map((problem) => (
              <li key={problem}>{problem}</li>
            ))}
          </ul>
        </div>
      )}

      {read.warnings.length > 0 && (
        <div className="avvisi" role="note">
          <h3>{t('upload.note')}</h3>
          <ul>
            {read.warnings.map((warning) => (
              <li key={warning}>{warning}</li>
            ))}
          </ul>
        </div>
      )}

      {/* **La risposta alla domanda che chi carica si sta facendo**, cioe'
          «ho scelto la cartella giusta?», e' una frase: «DU1048300 — due camere
          da 45 scatti, una nuvola, traiettoria presente». La lista dei campi
          sotto e' il dettaglio; la frase e' cio' che si legge. */}
      <h2>{sentence(t, read)}</h2>
      <dl>
        <dt>{t('upload.surveyType')}</dt>
        <dd>{read.survey_type ?? t('upload.undeclared')}</dd>
        <dt>{t('upload.crs')}</dt>
        <dd>{read.epsg === null ? t('upload.undeclared') : `EPSG:${read.epsg}`}</dd>
        <dt>{t('upload.clouds')}</dt>
        <dd>{read.clouds}</dd>
        <dt>{t('upload.trajectory')}</dt>
        <dd>{read.trajectory ? t('upload.present') : t('upload.absent')}</dd>
        <dt>{t('upload.swim')}</dt>
        <dd>{read.swim_input ? t('upload.present') : t('upload.absent')}</dd>
        <dt>{t('upload.total')}</dt>
        <dd>
          {t('upload.filesSize', { n: read.files, size: size(read.bytes) })}
        </dd>
      </dl>

      <h3>{t('upload.cameras')}</h3>
      {read.cameras.length === 0 ? (
        <p>{t('upload.noCameras')}</p>
      ) : (
        <table>
          <thead>
            <tr>
              <th>{t('upload.colCamera')}</th>
              <th>{t('upload.colFolder')}</th>
              <th>{t('upload.colFrames')}</th>
              <th>{t('upload.colPose')}</th>
            </tr>
          </thead>
          <tbody>
            {read.cameras.map((camera) => (
              <tr key={camera.folder}>
                <td>{camera.label}</td>
                <td>{camera.folder}</td>
                <td>{camera.frames}</td>
                <td>{camera.alignments ? t('upload.presentPl') : t('upload.absentPl')}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {read.unknown_datapacks.length > 0 && (
        <p className="ignoti">
          {t('upload.untreated', { names: read.unknown_datapacks.join(', ') })}
        </p>
      )}

      {blocked && <p className="prossimo">{t('upload.fixFolder')}</p>}
    </section>
  )
}


/** L'esito del preliminare in una riga, per chi deve decidere se proseguire. */
function sentence(t: ReturnType<typeof useT>, read: Recognised): string {
  const frames = read.cameras.map((one) => one.frames)
  const same = frames.length > 0 && frames.every((n) => n === frames[0])
  const cameras =
    read.cameras.length === 0
      ? t('upload.camerasNone')
      : read.cameras.length === 1
        ? t('upload.camerasOne', { frames: frames[0] ?? 0 })
        : t('upload.camerasMany', {
            n: read.cameras.length,
            // Scatti diversi per camera: si dice il totale, non un numero che
            // sarebbe vero per una sola di loro.
            frames: same ? (frames[0] ?? 0) : frames.reduce((a, b) => a + b, 0),
          })
  const clouds =
    read.clouds === 0
      ? t('upload.cloudsNone')
      : read.clouds === 1
        ? t('upload.cloudsOne')
        : t('upload.cloudsMany', { n: read.clouds })
  return t('upload.recognisedAs', {
    name: read.name || t('upload.unnamed'),
    cameras,
    clouds,
    trajectory: read.trajectory ? t('upload.trajectoryYes') : t('upload.trajectoryNo'),
  })
}


/**
 * I file dentro cio' che e' stato lasciato cadere, con il loro percorso.
 *
 * Il navigatore consegna voci, non file: una cartella e' una voce che si apre
 * su altre voci, e va scesa fino in fondo. Dove `webkitGetAsEntry` non c'e',
 * si prendono i soli file piatti — meno di quanto serva a un pacchetto, ma la
 * schermata dira' che manca il manifesto invece di tacere.
 */
async function dropped(transfer: DataTransfer): Promise<File[]> {
  const items = Array.from(transfer.items ?? [])
  const entries = items
    .map((item) => (typeof item.webkitGetAsEntry === 'function' ? item.webkitGetAsEntry() : null))
    .filter((entry): entry is FileSystemEntry => entry !== null)
  if (entries.length === 0) return Array.from(transfer.files ?? [])
  const found: File[] = []
  for (const entry of entries) await descend(entry, '', found)
  return found
}

async function descend(entry: FileSystemEntry, prefix: string, into: File[]): Promise<void> {
  if (entry.isFile) {
    const file = await new Promise<File>((resolve, reject) =>
      (entry as FileSystemFileEntry).file(resolve, reject),
    )
    into.push(withPath(file, `${prefix}${entry.name}`))
    return
  }
  if (!entry.isDirectory) return
  const reader = (entry as FileSystemDirectoryEntry).createReader()
  // `readEntries` consegna a lotti e finisce con un lotto vuoto: si legge
  // finche' ne arrivano.
  for (;;) {
    const batch = await new Promise<FileSystemEntry[]>((resolve, reject) =>
      reader.readEntries(resolve, reject),
    )
    if (batch.length === 0) break
    for (const child of batch) await descend(child, `${prefix}${entry.name}/`, into)
  }
}
